/**
 * HUD：资源栏、法则横幅、建造侧边栏、选中栏、小地图。
 *
 * 实现说明：本原型的 HUD 用原生 DOM 而非主设计方案里写的 React。
 * 理由是 HUD 只有约 40 个需要更新的节点，直接操作 DOM 没有心智负担，
 * 也省去一整套构建配置——真正的原型风险在模拟内核，不在界面框架。
 * 接入 React 时本文件是唯一需要重写的地方，其余模块不受影响。
 */

import { TICKS_PER_SEC, MAP_W, MAP_H, Kind } from '../shared/constants.ts';
import { DEFS, defOf, type UnitDef } from '../sim/data/types.ts';
import { LAWS } from '../sim/data/laws.ts';
import type { SnapMeta } from '../shared/protocol.ts';
import type { Renderer } from '../render/renderer.ts';
import { getSpriteCanvas } from '../render/sprites.ts';
import { startAtmosphere } from './atmosphere.ts';

const $ = (id: string): HTMLElement => document.getElementById(id)!;

export interface HudCallbacks {
  onBuildSelect: (def: UnitDef) => void;
  onProduce: (def: UnitDef) => void;
  onCancel: (def: UnitDef) => void;
  onMinimapClick: (tx: number, ty: number, right: boolean) => void;
  onRestart: () => void;
}

export class Hud {
  private tab: 'building' | 'infantry' | 'vehicle' = 'building';
  private buttons = new Map<number, HTMLElement>();
  private meta: SnapMeta | null = null;
  private minimapCtx: CanvasRenderingContext2D;
  private lastToast = 0;
  private bootAt = performance.now();

  constructor(private cb: HudCallbacks) {
    this.minimapCtx = ($('minimap') as HTMLCanvasElement).getContext('2d')!;
    startAtmosphere();

    for (const el of document.querySelectorAll<HTMLElement>('.tab')) {
      el.addEventListener('click', () => {
        this.tab = el.dataset.tab as typeof this.tab;
        for (const t of document.querySelectorAll('.tab')) t.classList.remove('active');
        el.classList.add('active');
        this.rebuildList();
      });
    }

    $('go-restart').addEventListener('click', () => this.cb.onRestart());

    const mm = $('minimap') as HTMLCanvasElement;
    const handle = (e: MouseEvent): void => {
      const r = mm.getBoundingClientRect();
      const px = ((e.clientX - r.left) / r.width) * mm.width;
      const py = ((e.clientY - r.top) / r.height) * mm.height;
      const t = this.minimapToTile(px, py);
      if (t) this.cb.onMinimapClick(t.tx, t.ty, e.button === 2);
    };
    mm.addEventListener('mousedown', handle);
    mm.addEventListener('contextmenu', (e) => e.preventDefault());

    this.rebuildList();
  }

  // ── 建造列表 ────────────────────────────────────────────

  private rebuildList(): void {
    const list = $('build-list');
    list.innerHTML = '';
    this.buttons.clear();
    for (const def of DEFS) {
      if (def.tab !== this.tab) continue;
      if (def.key === 'conyard') continue; // 建造厂不可再造

      const el = document.createElement('div');
      el.className = 'bi';
      el.innerHTML = `
        <canvas class="bi-icon" width="44" height="40"></canvas>
        <div class="bi-body">
          <div class="bi-name">${def.name}</div>
          <div class="bi-cost">
            <span>SEU ${def.costSeu}</span>
            ${def.costIntel > 0 ? `<span class="c-intel">INT ${def.costIntel}</span>` : ''}
            ${def.pop > 0 ? `<span class="c-pop">POP ${def.pop}</span>` : ''}
            ${def.power !== 0 ? `<span class="${def.power > 0 ? 'c-pow-plus' : 'c-pow'}">PWR ${def.power > 0 ? '+' : ''}${def.power}</span>` : ''}
          </div>
        </div>
        <div class="bi-prog"><i></i></div>
        <div class="bi-badge"></div>
        <div class="bi-lock"></div>`;

      const icon = el.querySelector('canvas')!;
      const ictx = icon.getContext('2d')!;
      const src = getSpriteCanvas(def.id, 0);
      const sc = Math.min(icon.width / src.width, icon.height / src.height) * 0.92;
      const dw = src.width * sc;
      const dh = src.height * sc;
      ictx.drawImage(src, (icon.width - dw) / 2, (icon.height - dh) / 2 + 2, dw, dh);

      el.title = `${def.name}\n${def.desc}\n生命 ${def.hp} / 护甲 ${['无甲', '轻甲', '重甲', '结构', '异相'][def.armor]}` +
        (def.dps > 0 ? `\nDPS ${def.dps}（${['动能', '穿甲', '爆破', '燃烧', '精神', '法则'][def.dmgType]}） / 射程 ${def.range}` : '') +
        (def.requires.length ? `\n前置：${def.requires.map((k) => DEFS.find((d) => d.key === k)!.name).join('、')}` : '');

      el.addEventListener('click', () => {
        if (el.classList.contains('locked')) {
          this.toast(`需要前置：${def.requires.map((k) => DEFS.find((d) => d.key === k)!.name).join('、')}`);
          return;
        }
        if (def.kind === Kind.Building) this.cb.onBuildSelect(def);
        else this.cb.onProduce(def);
      });
      el.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        if (def.kind !== Kind.Building) this.cb.onCancel(def);
      });

      list.appendChild(el);
      this.buttons.set(def.id, el);
    }
    if (this.meta) this.refreshList(this.meta);
  }

  private refreshList(meta: SnapMeta): void {
    const owned = new Set(meta.owned);
    for (const [typeId, el] of this.buttons) {
      const def = defOf(typeId);
      const unlocked = def.requires.every((r) => owned.has(r));
      const affordable = meta.seu >= def.costSeu && meta.intel >= def.costIntel;
      const popOk = def.pop === 0 || meta.pop + def.pop <= meta.popCap;

      el.classList.toggle('locked', !unlocked);
      el.classList.toggle('poor', unlocked && (!affordable || !popOk));

      const prod = meta.producing.find((p) => p.typeId === typeId);
      const prog = el.querySelector<HTMLElement>('.bi-prog i')!;
      const badge = el.querySelector<HTMLElement>('.bi-badge')!;
      if (prod) {
        prog.style.width = `${prod.progress / 10}%`;
        el.classList.add('producing');
        badge.textContent = prod.queued > 0 ? `+${prod.queued}` : '';
      } else {
        prog.style.width = '0%';
        el.classList.remove('producing');
        badge.textContent = '';
      }

      const lock = el.querySelector<HTMLElement>('.bi-lock')!;
      lock.textContent = unlocked ? '' : 'LOCK';
    }
  }

  // ── 每帧更新 ────────────────────────────────────────────

  update(meta: SnapMeta, renderer: Renderer, fps: number): void {
    this.meta = meta;

    $('res-seu').textContent = String(meta.seu);
    $('res-intel').textContent = String(meta.intel);
    $('res-seu-rate').textContent = meta.seuRate !== 0 ? ` ${meta.seuRate > 0 ? '+' : ''}${meta.seuRate}/s` : '';
    $('res-intel-rate').textContent = meta.intelRate !== 0 ? ` +${meta.intelRate}/s` : '';

    const powerEl = $('res-power');
    powerEl.textContent = `${meta.powerGen}/${meta.powerUse}`;
    powerEl.parentElement!.classList.toggle('warn', meta.powerUse > meta.powerGen);

    $('res-pop').textContent = `${meta.pop}/${meta.popCap}`;
    $('res-pop').parentElement!.classList.toggle('warn', meta.pop >= meta.popCap);

    $('perf-sim').textContent = meta.simMs.toFixed(1);
    $('perf-fps').textContent = String(fps);
    $('perf-ent').textContent = String(meta.entCount);
    $('perf-tick').textContent = String(meta.tick);

    this.updateLaw(meta);
    this.refreshList(meta);
    this.drawMinimap(meta, renderer);

    const go = $('gameover');
    if (meta.gameOver !== 0) {
      go.classList.remove('hidden');
      go.setAttribute('aria-hidden', 'false');
      $('go-title').textContent = meta.gameOver === 1 ? '收容完成' : '前线失守';
      $('go-title').className = meta.gameOver === 1 ? 'win' : 'lose';
      $('go-sub').textContent =
        meta.gameOver === 1
          ? `敌方建造厂已注销。观测时长 ${this.fmtTime(meta.tick)}。并非所有战线都应被重开。`
          : `己方建造厂已损毁。坚持 ${this.fmtTime(meta.tick)}。记录已封存。`;
    } else {
      go.classList.add('hidden');
      go.setAttribute('aria-hidden', 'true');
    }
  }

  private updateLaw(meta: SnapMeta): void {
    const law = LAWS[meta.lawId]!;
    const banner = $('law-banner');
    const secs = Math.max(0, Math.ceil(meta.lawTicksLeft / TICKS_PER_SEC));

    $('law-name').textContent = meta.lawId === 0 ? law.name : `${law.code} ${law.name}`;
    $('law-desc').textContent = law.desc;
    $('law-desc').title = law.desc;
    $('law-icon').style.color = `#${law.color.toString(16).padStart(6, '0')}`;
    banner.style.setProperty('--law-color', `#${law.color.toString(16).padStart(6, '0')}`);

    const total = meta.lawId === 0 ? 120 : 240;
    $('law-bar-fill').style.width = `${Math.max(0, Math.min(100, (secs / total) * 100))}%`;

    if (!law.autoRotate) {
      $('law-timer').textContent = '永久';
      banner.classList.remove('warning');
      return;
    }

    $('law-timer').textContent = this.fmtSecs(secs);
    banner.classList.toggle('warning', meta.lawWarning);

    if (meta.lawWarning) {
      if (meta.nextLawKnown && meta.nextLawId >= 0) {
        const next = LAWS[meta.nextLawId]!;
        $('law-desc').textContent = `预警 ${secs} 秒后切换为「${next.name}」：${next.desc}`;
        $('law-desc').title = $('law-desc').textContent;
      } else {
        $('law-desc').textContent = `预警 ${secs} 秒后法则将改变（建造观测站可提前得知具体条目）`;
        $('law-desc').title = $('law-desc').textContent;
      }
    }
  }

  // ── 小地图 ──────────────────────────────────────────────

  private mmScale = 0;
  private mmOffX = 0;

  private drawMinimap(meta: SnapMeta, r: Renderer): void {
    const ctx = this.minimapCtx;
    const size = 180;
    ctx.clearRect(0, 0, size, size);

    if (!r.minimapTerrain) return;

    // 信息湍流：雷达失效
    if (meta.radarBlackout) {
      ctx.fillStyle = '#0d100e';
      ctx.fillRect(0, 0, size, size);
      ctx.fillStyle = '#b9483f';
      ctx.font = '11px "Space Mono", Consolas, monospace';
      ctx.textAlign = 'center';
      ctx.fillText('RADAR BLACKOUT', size / 2, size / 2 - 8);
      ctx.fillText('ZLR-04 信息湍流', size / 2, size / 2 + 10);
      ctx.textAlign = 'left';
      return;
    }

    // 等距变换：把 96×96 的缩略图铺成一个 45° 菱形，与主视图方向一致
    const s = size / (MAP_W + MAP_H);
    this.mmScale = s;
    this.mmOffX = size / 2;

    ctx.save();
    ctx.setTransform(s, s, -s, s, size / 2, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(r.minimapTerrain, 0, 0);
    if (!meta.hasRadar) {
      ctx.restore();
      ctx.fillStyle = 'rgba(8,10,9,0.86)';
      ctx.fillRect(0, 0, size, size);
      ctx.fillStyle = '#777d74';
      ctx.font = '11px "Space Mono", Consolas, monospace';
      ctx.textAlign = 'center';
      ctx.fillText('需要观测站', size / 2, size / 2 + 4);
      ctx.textAlign = 'left';
      return;
    }
    ctx.imageSmoothingEnabled = true;
    ctx.globalAlpha = 0.95;
    ctx.drawImage(r.fogThumbnail, 0, 0);
    ctx.restore();

    // 单位光点
    for (const b of r.entityBlips()) {
      const x = (b.tx - b.ty) * s + size / 2;
      const y = (b.tx + b.ty) * s;
      ctx.fillStyle = b.owner === 0 ? '#b8d25d' : '#b9483f';
      const d = b.kind === Kind.Building ? 3 : 2;
      ctx.fillRect(x - d / 2, y - d / 2, d, d);
    }

    // 取景框
    const corners = r.viewportCorners();
    ctx.strokeStyle = 'rgba(184,210,93,0.75)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    corners.forEach((c, i) => {
      const x = (c.tx - c.ty) * s + size / 2;
      const y = (c.tx + c.ty) * s;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.stroke();
  }

  private minimapToTile(px: number, py: number): { tx: number; ty: number } | null {
    if (this.mmScale === 0) return null;
    const s = this.mmScale;
    const dx = px - this.mmOffX;
    const tx = (py / s + dx / s) / 2;
    const ty = (py / s - dx / s) / 2;
    if (tx < 0 || ty < 0 || tx >= MAP_W || ty >= MAP_H) return null;
    return { tx: Math.floor(tx), ty: Math.floor(ty) };
  }

  // ── 选中栏 ──────────────────────────────────────────────

  updateSelection(ids: number[], r: Renderer): void {
    const info = $('sel-info');
    const list = $('sel-list');
    if (ids.length === 0) {
      info.textContent = '未选定编制　　左键框选 · 右键移动/攻击 · A 强攻 · S 停止 · H 回基地';
      list.innerHTML = '';
      return;
    }

    const groups = new Map<number, number>();
    for (const id of ids) {
      const v = r.viewOf(id);
      if (!v) continue;
      groups.set(v.typeId, (groups.get(v.typeId) ?? 0) + 1);
    }

    if (ids.length === 1) {
      const v = r.viewOf(ids[0]!);
      if (v) {
        const d = defOf(v.typeId);
        info.textContent = `${d.name}　生命 ${v.hp}/${v.maxHp}　护甲 ${['无甲', '轻甲', '重甲', '结构', '异相'][d.armor]}` +
          (d.dps > 0 ? `　DPS ${d.dps}（${['动能', '穿甲', '爆破', '燃烧', '精神', '法则'][d.dmgType]}）　射程 ${d.range}` : '') +
          `　—　${d.desc}`;
      }
    } else {
      info.textContent = `已选定 ${ids.length} 个编制单位`;
    }

    list.innerHTML = '';
    for (const [typeId, n] of groups) {
      const d = defOf(typeId);
      const el = document.createElement('div');
      el.className = 'sel-chip';
      el.innerHTML = `<i style="background:#${d.color.toString(16).padStart(6, '0')}"></i>${d.name}${n > 1 ? ` ×${n}` : ''}`;
      list.appendChild(el);
    }
  }

  // ── 提示 ────────────────────────────────────────────────

  toast(msg: string): void {
    const t = $('toast');
    const now = performance.now();
    if (now - this.lastToast < 250 && t.textContent === msg) return;
    this.lastToast = now;
    t.textContent = msg;
    t.classList.add('show');
    window.clearTimeout((t as unknown as { _timer: number })._timer);
    (t as unknown as { _timer: number })._timer = window.setTimeout(() => t.classList.remove('show'), 2200);
  }

  setBuildHint(msg: string): void {
    $('build-hint').textContent = msg;
  }

  setBootLog(msg: string): void {
    const log = document.getElementById('boot-log');
    if (log) log.textContent = msg;
  }

  hideLoading(): void {
    const el = $('loading');
    const wait = Math.max(0, 1650 - (performance.now() - this.bootAt));
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const close = (): void => {
      el.classList.add('done');
      window.setTimeout(() => {
        el.style.display = 'none';
      }, reduced ? 0 : 720);
    };
    if (reduced) close();
    else window.setTimeout(close, wait);
  }

  private fmtSecs(s: number): string {
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }

  private fmtTime(ticks: number): string {
    return this.fmtSecs(Math.floor(ticks / TICKS_PER_SEC));
  }
}
